import { useEffect } from "react";

const BASE = "https://www.prodjsabilene.com";
const DEFAULT_IMAGE = `${BASE}/og-card.webp`;
const DEFAULT_IMAGE_ALT = "Sparkler send-off at an Abilene wedding — Pro DJ's & Event Services";

function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(attr, key) {
  document.querySelectorAll(`meta[${attr}="${key}"]`).forEach((el) => el.remove());
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.querySelector(`script[data-seo="${id}"]`);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo", id);
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(data);
}

const absUrl = (u) =>
  !u ? null : u.startsWith("http") ? u : `${BASE}${u.startsWith("/") ? "" : "/"}${u}`;

/**
 * Lightweight per-page SEO for the SPA (head tags + BreadcrumbList JSON-LD).
 * - `noindex`: marks 404/utility views as noindex and drops the canonical.
 * - `article`: { publishedTime, modifiedTime, author, section } → og:type=article + article:* tags.
 */
export default function Seo({ title, description, path = "/", image, breadcrumbs, noindex = false, article }) {
  useEffect(() => {
    const brandRegex = /Pro DJ'?s( & Event Services| Abilene)?$/i;
    const fullTitle = title
      ? (brandRegex.test(title.trim()) ? title : `${title} | Pro DJ's Abilene`)
      : "Pro DJ's Abilene | Wedding DJs, Decor & Coordination";
    document.title = fullTitle;
    const url = `${BASE}${path}`;
    const img = absUrl(image) || DEFAULT_IMAGE;
    const isDefaultImg = img === DEFAULT_IMAGE;

    setMeta("name", "robots", noindex ? "noindex, follow" : "index, follow");
    setMeta("name", "description", description);
    setMeta("property", "og:type", article ? "article" : "website");
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", img);
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", img);
    setCanonical(noindex ? null : url);

    // Image hints: the 1200×630 dimensions only apply to the default social card.
    if (isDefaultImg) {
      setMeta("property", "og:image:width", "1200");
      setMeta("property", "og:image:height", "630");
      setMeta("property", "og:image:alt", DEFAULT_IMAGE_ALT);
      setMeta("name", "twitter:image:alt", DEFAULT_IMAGE_ALT);
    } else {
      removeMeta("property", "og:image:width");
      removeMeta("property", "og:image:height");
      setMeta("property", "og:image:alt", fullTitle);
      setMeta("name", "twitter:image:alt", fullTitle);
    }

    if (article) {
      setMeta("property", "article:published_time", article.publishedTime);
      setMeta("property", "article:modified_time", article.modifiedTime || article.publishedTime);
      setMeta("property", "article:author", article.author);
      setMeta("property", "article:section", article.section);
    } else {
      ["article:published_time", "article:modified_time", "article:author", "article:section"].forEach((k) =>
        removeMeta("property", k)
      );
    }

    const crumbs =
      breadcrumbs && breadcrumbs.length
        ? {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: breadcrumbs.map((b, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: b.name,
              item: `${BASE}${b.path}`,
            })),
          }
        : null;
    setJsonLd("breadcrumb", crumbs);
  });

  return null;
}
